import { KeyConfig } from '@rapoo/element'
import type { InjectionKey, Ref } from 'vue'

// 出厂键/展示改写的配置形态由 manifest 定义，统一从 SDK 取，避免两处维护
export type { KeyDisplayOverride, KeysCapability } from '@rapoo/device-sdk'

export const SelectedKeySymbol: InjectionKey<Ref<SelectedKey>> = Symbol.for('selectedKey')
export const RightSelectedKeySymbol: InjectionKey<Ref<string | undefined>> =
  Symbol.for('rightSelectedKey')
export const KeySettingsSymbol: InjectionKey<Ref<KeySetting[]>> = Symbol.for('keySettings')
export const ActiveTabValueSymbol: InjectionKey<Ref<string>> = Symbol.for('activeTabValue')
export const KeyLayerSymbol: InjectionKey<Ref<'default' | 'fn'>> = Symbol.for('keyLayer')
export const KeyPlatformSymbol: InjectionKey<Ref<KeyPlatform>> = Symbol.for('keyPlatform')
export const SuppressSelectedKeyUxSymbol: InjectionKey<Ref<boolean>> =
  Symbol.for('suppressSelectedKeyUx')
export const AdvancedEditingSettingSymbol: InjectionKey<Ref<KeySetting | null>> =
  Symbol.for('advancedEditingSetting')
export const RefreshLayerKeySettingsSymbol: InjectionKey<() => Promise<void>> =
  Symbol.for('refreshLayerKeySettings')
export const UpdateSelectedKeyMappingSymbol = 'updateSelectedKeyMapping'
export const UpdateKeyMappingWithTargetSymbol = 'updateKeyMappingWithTarget'
export const RestoreKeyMappingWithTargetSymbol = 'restoreKeyMappingWithTarget'

export type SelectedKey = KeyConfig | null
export type KeyPlatform = 'win' | 'mac'

export type UpdateSelectedKeyMapping = (data: KeyChangePayload) => void
export type UpdateKeyMappingWithTarget = (data: KeyChangePayload, targetKey: SelectedKey) => void
export type RestoreKeyMappingWithTarget = (targetKey: SelectedKey) => void | Promise<void>

export type KeyType =
  | 'normal'
  | 'media'
  | 'configuration'
  | 'DKS'
  | 'MT'
  | 'TGL'
  | 'RS'
  | 'SOCD'
  | 'macro'

/**
 * keycode name/alias 对象结构
 */
export type KeycodeLabel = {
  standard?: string
  windows?: string
  macx?: string
  linux?: string
}

/**
 * keycode.json 中单条键位元数据结构。
 */
export type KeycodeMetaItem = {
  keywords: string
  name: string | KeycodeLabel
  alias?: string | KeycodeLabel
  type: string
  stype: string
  code: string
  sys_code?: {
    windows: string[]
    linux: string[]
    macx: string[]
  }
  [key: string]: unknown
}

/**
 * 统一后的映射元数据，供 UI keySetting 生成使用。
 */
export type ResolveMappedMetaResult = {
  keywords: string
  keyType: Extract<KeyType, 'normal' | 'media' | 'configuration'>
  // mappedKeyCode?: string
  icon?: string
  confIndex?: number
}

export type LocalizedSpecialKeyMapItem = SpecialKeyMapItem & {
  labelKey?: string
}

// 键盘设置统一配置
export interface KeySetting {
  keywords: string // 原按键标识
  keyType?: KeyType
  isModified?: boolean // 原按键是否被修改（含出厂默认展示态）
  /** Fn 层出厂映射且当前仍为出厂功能时为 true */
  isFactoryDefault?: boolean
  /** 强制展示的文案，优先于 target 的 name/alias（出厂详写、展示改写规则） */
  displayLabel?: string
  target?: KeyConfig | LocalizedSpecialKeyMapItem | null // 原按键被映射的键
  raw?: any
  [key: string]: any
}

/** 出厂功能 keywords 解析出的 type/code，供匹配与下发使用 */
export interface ResolvedFactoryKey {
  keywords: string
  type: number
  code: number
  confIndex?: number
}

// 改键统一 payload
export interface KeyChangePayload {
  keywords: string // 原按键标识
  keyType: KeyType
  target: KeyConfig | LocalizedSpecialKeyMapItem | null // 原按键被映射的键
  raw: any
  [key: string]: any
}

export interface DragImageOptions {
  text?: string | null
  icon?: string
  width?: number
  height?: number
  offsetX?: number
  offsetY?: number
}

export type LayoutKey = {
  key?: number
  keywords: string
  // name?: string | { standard?: string; windows?: string; macx?: string; linux?: string }
  // type?: string | number
  // code?: string | number | Array<string | number>
  confIndex?: string | number
}

export type SpecialKeyMapItem = {
  keywords: string
  keyType: KeyType
  icon?: string
  confIndex?: number // 配置改键索引
}
